import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export class ApiService {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  async login(token: string): Promise<{ email: string; success: boolean }> {
    try {
      const response = await api.post('/login', { token });
      if (response.data.success) {
        this.setToken(token);
        return { email: response.data.user.email, success: true };
      }
      return { email: '', success: false };
    } catch (error) {
      console.error('Login failed:', error);
      return { email: '', success: false };
    }
  }

  async getSectors(): Promise<{
    sectors: string[];
    sector_project_mapping: Record<string, string>;
    sector_gke_mapping: Record<string, string>;
  }> {
    try {
      const response = await api.get('/sectors');
      return response.data;
    } catch (error) {
      console.error('Failed to get sectors:', error);
      throw error;
    }
  }

  async troubleshoot(callId: string, domainId: string, sector: string): Promise<any> {
    try {
      const response = await api.post('/troubleshoot', {
        call_id: callId,
        domain_id: domainId,
        sector: sector
      });
      return response.data;
    } catch (error) {
      console.error('Troubleshoot failed:', error);
      throw error;
    }
  }

  async analyzeFlowServiceLogs(
    callId: string, 
    domainId: string, 
    projectId: string,
    callStartTimestamp?: string,
    callEndTimestamp?: string
  ): Promise<{
    events: any[];
    stats: any;
  }> {
    try {
      const response = await api.post('/flow-service-logs', {
        call_id: callId,
        domain_id: domainId,
        project_id: projectId,
        call_start_timestamp: callStartTimestamp,
        call_end_timestamp: callEndTimestamp
      });
      return response.data;
    } catch (error) {
      console.error('Flow Service log analysis failed:', error);
      throw error;
    }
  }

  async analyzeTagsServiceLogs(
    callId: string, 
    domainId: string, 
    projectId: string,
    callStartTimestamp?: string,
    callEndTimestamp?: string
  ): Promise<{
    events: any[];
    stats: any;
    customSummaryDetails?: any[];
    defaultSummaryDetails?: any[];
  }> {
    try {
      const response = await api.post('/tags-service-logs', {
        call_id: callId,
        domain_id: domainId,
        project_id: projectId,
        call_start_timestamp: callStartTimestamp,
        call_end_timestamp: callEndTimestamp
      });
      return response.data;
    } catch (error) {
      console.error('Tags Service log analysis failed:', error);
      throw error;
    }
  }

  async analyzeVoiceStreamLogs(
    callId: string, 
    domainId: string, 
    projectId: string,
    callStartTimestamp?: string,
    callEndTimestamp?: string
  ): Promise<{
    events: any[];
    stats: any;
  }> {
    try {
      const response = await api.post('/voicestream-service-logs', {
        call_id: callId,
        domain_id: domainId,
        project_id: projectId,
        call_start_timestamp: callStartTimestamp,
        call_end_timestamp: callEndTimestamp
      });
      return response.data;
    } catch (error) {
      console.error('Voice Stream log analysis failed:', error);
      throw error;
    }
  }

  async analyzeTranscriptionServiceLogs(
    callId: string, 
    domainId: string, 
    projectId: string,
    callStartTimestamp?: string,
    callEndTimestamp?: string
  ): Promise<{
    events: any[];
    stats: any;
  }> {
    try {
      const response = await api.post('/transcription-service-logs', {
        call_id: callId,
        domain_id: domainId,
        project_id: projectId,
        call_start_timestamp: callStartTimestamp,
        call_end_timestamp: callEndTimestamp
      });
      return response.data;
    } catch (error) {
      console.error('Transcription Service log analysis failed:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();
